import { type PostgrestFilterBuilder } from '@supabase/postgrest-js';
import { SupabaseClient } from '@supabase/supabase-js';
import camelcaseKey, { type CamelCaseKeys } from 'camelcase-keys';

import { Database, Json, withLogs } from '@core';

export type UiNodeType =
    | 'page'
    | 'grid'
    | 'row'
    | 'column'
    | 'activity_navigation'
    | 'swing_foundations'
    | 'module'
    | 'sequence'
    | 'graph'
    | 'graph_line'
    | 'parameter';

export type OtherUiNodeType = Exclude<UiNodeType, 'graph' | 'graph_line' | 'parameter'>;

export type BaseNode = {
    id: string;
    type: UiNodeType;
    metadata: Json | null;
    showOn: ('kiosk' | 'floor')[];
    i18n?: string | null;
};

export const mapBaseUiNode = (uiNode: CamelCaseKeys<Database['public']['Tables']['ui_nodes']['Row']>): BaseNode => ({
    id: uiNode.id,
    type: uiNode.type,
    metadata: uiNode.metadata,
    showOn: uiNode.showOn,
    i18n: uiNode.i18N,
});

export interface WithLimitResult<T> {
    data: T[];
    error: Error | null;
    count: number;
}

export async function withLimit<T>(
    limit: number,
    query: PostgrestFilterBuilder<any, any, T[]>,
): Promise<WithLimitResult<T>> {
    const allData: T[] = [];
    let offset = 0;

    while (offset < limit) {
        const { data, error } = await query.range(offset, offset + Math.min(999, limit - offset - 1));

        if (error) {
            return { data: allData, error, count: allData.length };
        }

        if (!data || data.length === 0) {
            break;
        }

        allData.push(...data);
        offset += data.length;

        if (data.length < 1000) {
            break;
        }
    }

    return {
        data: allData,
        error: null,
        count: allData.length,
    };
}

export const fetchAllUiNodes = withLogs(async (supabase: SupabaseClient<Database>): Promise<BaseNode[]> => {
    const { data, error } = await withLimit(
        3000,
        supabase.from('ui_nodes').select('*').order('id', { ascending: true }),
    );

    if (error) {
        console.error('error fetching ui_nodes:', error);
        return [];
    }
    const allUiNodes = camelcaseKey(data, { deep: true });
    return allUiNodes.map(mapBaseUiNode);
}, 'fetchAllUiNodes');

export const fetchUiNodeType = withLogs(
    async (supabase: SupabaseClient<Database>, type: OtherUiNodeType): Promise<BaseNode[]> => {
        const { data, error } = await withLimit(
            3000,
            supabase.from('ui_nodes').select('*').eq('type', type).order('id', { ascending: true }),
        );

        if (error) {
            console.error(`error fetching ui_nodes for type ${type}:`, error);
            return [];
        }
        const uiNodesTypes = camelcaseKey(data, { deep: true });
        return uiNodesTypes.map(mapBaseUiNode);
    },
    'fetchUiNodeType',
);
